import { ObjectHelper } from 'helpers';
import { AuthContext } from 'providers/authProvider';
import React, { useEffect, useState } from 'react';
import { Eye, Plus } from 'react-feather';
import { Link } from 'react-router-dom';
import { hasPermission } from 'services/auth';

import { Button, NOTIFICATION_TYPE, Notification, Table } from 'components';
import { CsvExportButton } from 'components/CsvExport/CsvExportButton';
import { useGetTransporters } from 'features/carriers/hooks/useGetCarriers';
import { SearchBar } from 'features/common/search/SearchBar';

import CarrierActionButton from './CarrierActionButton';
import { CarriersCsvImport } from './CarriersCsvImport';

const columns = [
  { title: 'Company Name', dataIndex: 'companyName', sortable: true },
  { title: 'First Name', dataIndex: 'firstName' },
  { title: 'Last Name', dataIndex: 'lastName' },
  { title: 'Email', dataIndex: 'email', hidden: true },
  { title: 'Street Address', dataIndex: 'street1' },
  { title: 'City', dataIndex: 'city1' },
  { title: 'State', dataIndex: 'state1' },
  { title: 'Zip', dataIndex: 'zip1' },
  { title: 'Country', dataIndex: 'country1' },
  { title: 'Phone Number', dataIndex: 'phoneNumber' },
  { title: 'SCAC Code', dataIndex: 'SCACCode', hidden: true },
  { title: 'Vehicle License Plate Number', dataIndex: 'plate', hidden: true },
  { title: 'Transport Truck License Plate Number', dataIndex: 'truckPlate', hidden: true },
  { title: 'Trailer License Plate Number', dataIndex: 'trailerPlate', hidden: true },
  { title: 'Drivers License Number', dataIndex: 'driversLicense', hidden: true },
  { title: 'Passport Number', dataIndex: 'passport', hidden: true },
  { title: 'Fast ID', dataIndex: 'fastId', hidden: true },
  { title: 'Organization Code', dataIndex: 'orgCode', hidden: true },
  {
    title: 'Actions',
    dataIndex: '',
    render: (carrier) => (
      <div className="d-flex align-items-center gap-2">
        <Link to={`${carrier.id}`} className="text-white">
          <Eye size={20} />
        </Link>
        <CarrierActionButton carrier={carrier} />
      </div>
    ),
  },
];

const CarriersTable = () => {
  const { user } = React.useContext(AuthContext);

  const [sortOption, setSortOption] = React.useState({
    order: 'desc',
    orderBy: 'companyName',
  });
  const [pagination, setPagination] = React.useState({
    page: 0,
    perPage: 10,
  });
  const [search, setSearchTerm] = useState('');
  const queryObj = React.useMemo(() => {
    return ObjectHelper.trimQuery({
      ...pagination,
      ...sortOption,
      search: search,
    });
  }, [pagination, sortOption, search]);

  const { error, data, isLoading, isFetching, refetch } = useGetTransporters(queryObj);

  useEffect(() => {
    if (!error) return;
    Notification(NOTIFICATION_TYPE.DANGER, error?.message || 'System Error');
  }, [error]);

  const handleChangePagination = React.useCallback((field, value) => {
    if (field === 'page') {
      setPagination((prev) => ({ ...prev, page: value }));
    } else if (field === 'perPage') {
      setPagination({ page: 0, perPage: value });
    }
  }, []);

  return (
    <>
      <div className="d-flex gap-3 mb-4">
        <Link to="add">
          <Button
            className="d-flex gap-2"
            disabled={!hasPermission(user, { resource: 'carrier', action: 'create' })}
          >
            <Plus size={20} />
            Add
          </Button>
        </Link>
        <CarriersCsvImport
          columns={columns}
          onCsvImportSuccess={() => handleChangePagination('page', 0)}
        />
        <SearchBar fetch={refetch} onSearch={setSearchTerm} hasBorder={true} />
      </div>
      <Table
        data={(data?.data ?? []).map(({ carrier, ...data }) => ({
          ...data,
          ...carrier,
        }))}
        columns={columns}
        loading={isFetching || isLoading}
        checkable={true}
        sortOption={sortOption}
        setSortOption={setSortOption}
        pagination={{
          total: data?.totalCount || 0,
          page: pagination.page,
          perPage: pagination.perPage,
          onPageChange: (page) => handleChangePagination('page', page),
          onPerPageChange: (perPage) => handleChangePagination('perPage', perPage),
        }}
        renderCsvExporter={(columns, data) => (
          <CsvExportButton data={data} columns={columns} filename="carriers.csv">
            Export CSV
          </CsvExportButton>
        )}
      />
    </>
  );
};

export default CarriersTable;
